import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { get, flow } from 'lodash';
import { Row, Col, Button } from 'reactstrap';

// Models
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';

// Utils
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { getUsurpImages } from 'site-modules/shared/utils/inventory/srp-images';
import { getPhotoProvider } from 'site-modules/shared/utils/inventory-image-utils';
import {
  getVdpLink,
  getPaymentDetails,
  getDisplayBelowMarketCopy,
  getVehicleName,
  getKeyPoints,
  getVehicleNameAriaLabel,
  getIsLeaseCardWithLeasePayment,
  getIsLoanCardWithLoanPayment,
  getTCPAParentDealershipName,
  isUsedCTAAvailable,
  isCTAAvailable,
} from 'site-modules/shared/utils/inventory/usurp-inventory-card';
import { isRecurrentAvailable } from 'client/site-modules/shared/utils/inventory/is-recurrent-available';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { withInventoryListTracking } from 'site-modules/shared/components/inventory-list-tracking/inventory-list-tracking';
import { withListTrackingData } from 'site-modules/shared/components/inventory-list-tracking/list-tracking-data';
import { getAppliedRadius } from 'site-modules/shared/utils/inventory/srp-utils';
import {
  getCurrentPaymentType,
  getSelectedPaymentType,
} from 'site-modules/shared/utils/inventory-utils/get-current-payment-type';
import { isDriveway, isInstockOnline, isDeliveryEligible } from 'site-modules/shared/utils/dealer-details-utils';

// Constants
import { DEFAULT_COLOR } from 'site-modules/shared/components/inventory/constants/usurp-inventory-card';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { LEASE_PAYMENT } from 'site-modules/shared/constants/inventory/payment-types';

// Components
import { ColorInfo } from 'site-modules/shared/components/inventory/color-info/color-info';
import { UsurpInventoryCardPhoto } from 'site-modules/shared/components/usurp-inventory-card/usurp-inventory-card-photo';
import { UsurpInventoryCardInfo } from 'site-modules/shared/components/inventory/usurp-inventory-card-info/usurp-inventory-card-info';
import { VehicleNameDisplay } from 'site-modules/shared/components/inventory/vehicle-name-display/vehicle-name-display';
import { UsurpCardKeyPoint } from 'site-modules/shared/components/inventory/usurp-card-key-point/usurp-card-key-point';
import { ClickableCard } from 'site-modules/shared/components/inventory/clickable-card/clickable-card';
import { Link } from 'site-modules/shared/components/link/link';
import { ContactDealerSrp } from 'site-modules/shared/components/inventory/contact-dealer/contact-dealer-srp';

import './vin-preview-card.scss';

export function VinPreviewCardUI({
  inventoryData,
  visitorLocation,
  isMobile,
  disableInventoryMLPhotoSort,
  selectedFacets,
  creativeId,
}) {
  const radius = useMemo(() => getAppliedRadius({ inventoryData, visitorLocation, selectedFacets }), [
    inventoryData,
    selectedFacets,
    visitorLocation,
  ]);
  const attributes = inventoryData?.attributes ?? {};
  const paymentType = getCurrentPaymentType(selectedFacets, attributes?.paymentType);
  const selectedPaymentType = getSelectedPaymentType(selectedFacets);
  const vehicle = inventoryData?.inventories?.results?.[0];

  if (!inventoryData || !vehicle) return null;

  const { type, vehicleInfo, dealerInfo, prices, vin } = vehicle;
  const savings = get(vehicle, 'computedDisplayInfo.priceValidation.priceDiscount', 0);
  const exteriorColor = get(vehicleInfo, 'vehicleColors.exterior');
  const exteriorColorName = get(exteriorColor, 'name', DEFAULT_COLOR);
  const interiorColor = get(vehicleInfo, 'vehicleColors.interior');
  const interiorColorName = get(interiorColor, 'name', DEFAULT_COLOR);
  const isNewType = isNew(type);

  const dealerAddress = get(dealerInfo, 'address');
  const dealerStateCode = get(dealerAddress, 'stateCode');
  const { photoUrls, hasPhotos, photoType } = getUsurpImages(vehicle, disableInventoryMLPhotoSort);

  const isEnhancedName = true;
  const isGrayIcons = true;
  const rooftopId = get(vehicle, 'dealerInfo.rooftopId', '');
  const franchiseId = get(vehicle, 'dealerInfo.franchiseId', '');

  const isElectric = get(vehicle, 'vehicleInfo.partsInfo.engineType') === 'electric';
  const isRecurrent = isRecurrentAvailable(vehicle);
  const showRecurrentData = isElectric && !isNewType && isRecurrent;

  const keyPoints = getKeyPoints({
    vehicle,
    visitorLocation,
    radius,
    isSrp: true,
    showRecurrentData,
    isGrayIcons,
  });

  const { paymentValue } = getPaymentDetails({
    prices,
    usePaymentType: isNewType,
    selectedPaymentType,
    paymentType,
  });
  const displayBelowMarketCopy = getDisplayBelowMarketCopy({
    paymentValue,
    isNew: isNewType,
    dealerStateCode,
    savings,
  });
  const vdpLink = getVdpLink({ vehicle, paymentType, radius });

  const isLeaseCardWithLeasePayment = getIsLeaseCardWithLeasePayment({ selectedPaymentType, prices });
  const isLoanCardWithLoanPayment = getIsLoanCardWithLoanPayment({ selectedPaymentType, prices });
  const SRP_CARD_PHOTO_LIMIT = 15;

  const [firstLine, secondLine] = getVehicleName(vehicle, isEnhancedName, true);
  const vehicleNameDisplay = (
    <VehicleNameDisplay
      classNames="size-20 text-blue-30"
      vehicle={vehicle}
      firstLine={firstLine}
      secondLine={secondLine}
    />
  );

  const ariaLabel = getVehicleNameAriaLabel({ vehicle, isEnhancedName, prices, selectedPaymentType });
  const parentDealershipName = get(vehicle, DATA_PATH.PARENT_DEALERSHIP_NAME, '');
  const isDrivewayDealer = isDriveway(parentDealershipName);
  const tcpaParentDealershipName = getTCPAParentDealershipName({ parentDealershipName });
  const showUsedCta = isUsedCTAAvailable({ vehicle });
  const showCta = isCTAAvailable({ vehicle });
  const distance = Math.round(get(dealerInfo, 'distance', 0));
  const zipCode = visitorLocation?.zipCode;
  const isDeliveryAvailable = isDeliveryEligible(vehicle, zipCode);
  const isInStockOnline =
    (!isNewType || isDrivewayDealer) && isInstockOnline({ distance, radius, isDeliveryAvailable });

  return (
    <ClickableCard
      className={classnames(
        'd-flex flex-column flex-md-row vin-preview-card overflow-hidden h-100 w-100 bg-white p-md-1',
        {
          'rounded-12': !isMobile,
        }
      )}
      data-dealer-rooftop-id={rooftopId}
      data-tracking-parent={creativeId}
    >
      <Fragment>
        <UsurpInventoryCardPhoto
          photos={photoUrls.slice(0, SRP_CARD_PHOTO_LIMIT)}
          hasPhotos={hasPhotos}
          provider={getPhotoProvider(vehicle)}
          vehicle={vehicle}
          visitorStateCode={visitorLocation.stateCode}
          isMobile={isMobile}
          vin={vin}
          photoType={photoType}
          vdpLink={vdpLink}
          importance="high"
          showInventoryHeart
          showInventoryBadge
          showInTransit
          heartAriaLabel={ariaLabel}
          classNames="col-md-7 p-0 h-100"
          photoImgClassnames="h-100"
          isVinPreview
          withAspectRatio
        />
        <div className="vehicle-info d-flex flex-column pt-0_75 pe-1 pb-md-1 pb-1_5 ps-1_5 ps-md-1">
          <div className={classnames('visible-vehicle-info d-flex flex-column')}>
            <UsurpInventoryCardInfo
              vehicle={vehicle}
              selectedPaymentType={selectedPaymentType}
              ariaLabel={ariaLabel}
              vehicleNameDisplay={vehicleNameDisplay}
              vdpLink={vdpLink}
              displayBelowMarketCopy={displayBelowMarketCopy}
              isLeaseCardWithLeasePayment={isLeaseCardWithLeasePayment}
              isLoanCardWithLoanPayment={isLoanCardWithLoanPayment}
              isVinPreview
            />
            <Row className="text-gray-darker list-unstyled mb-0" tag="ul">
              {isNewType && (
                <>
                  <Col style={{ lineHeight: '1.125rem' }} className="col-auto pe-0" tag="li">
                    {exteriorColorName !== DEFAULT_COLOR && (
                      <ColorInfo
                        color={exteriorColor}
                        colorName={exteriorColorName}
                        colorSide="Exterior"
                        label={exteriorColorName}
                        style={{
                          marginRight: '1rem',
                          marginTop: '0.5rem',
                          display: 'inline-block',
                          lineHeight: '1.125rem',
                        }}
                        isSrpExpanded
                      />
                    )}
                  </Col>
                  <Col style={{ lineHeight: '1.125rem' }} className="col-auto" tag="li">
                    {interiorColorName !== DEFAULT_COLOR && (
                      <ColorInfo
                        color={interiorColor}
                        colorName={interiorColorName}
                        colorSide="Interior"
                        label={interiorColorName}
                        style={{ marginTop: '0.5rem', display: 'inline-block', lineHeight: '1.125rem' }}
                        isSrpExpanded
                      />
                    )}
                  </Col>
                </>
              )}
              <UsurpCardKeyPoint
                keyPoints={keyPoints}
                isGrayIcons={isGrayIcons}
                tag={Col}
                columnSizes={{ xs: 12 }}
                htmlTag="li"
              />
            </Row>
            <div className="d-flex flex-column mt-2">
              {showCta && (
                <ContactDealerSrp
                  vinData={vehicle}
                  rootClassName="g-0 margin-top-auto pb-1 d-block"
                  radius={radius}
                  isMobile={isMobile}
                  showLeasePayments={selectedPaymentType === LEASE_PAYMENT}
                  buttonColor="card-btn w-100 text-capitalize fw-medium rounded-8 size-16 px-1"
                  ariaLabel={ariaLabel}
                  showInTransit={isNewType}
                  photoUrl={photoUrls[0]}
                  isInStockOnline={isNewType && isInStockOnline}
                  {...(showUsedCta ? { tcpaParentDealershipName } : {})}
                />
              )}
              <Button
                className="card-btn w-100 fw-medium rounded-8 size-16 py-0_5"
                tag={Link}
                color="outline-blue-50"
                to={vdpLink}
                data-dealer-franchise-id={franchiseId}
                data-dealer-rooftop-id={rooftopId}
                data-tracking-id="srp_view_vin_details"
                data-tracking-value="vehicle_card_click"
              >
                See full vehicle details
              </Button>
            </div>
          </div>
        </div>
      </Fragment>
    </ClickableCard>
  );
}

VinPreviewCardUI.propTypes = {
  inventoryData: PropTypes.shape({
    inventories: PropTypes.shape({
      results: InventoryEntities.InventoriesVin,
      totalNumber: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    facets: InventoryEntities.Facets,
    seoInfo: PropTypes.shape({}),
  }),
  visitorLocation: VisitorEntities.Location,
  isMobile: PropTypes.bool,
  disableInventoryMLPhotoSort: PropTypes.bool,
  selectedFacets: PropTypes.shape({}),
  creativeId: PropTypes.string,
};

VinPreviewCardUI.defaultProps = {
  inventoryData: null,
  visitorLocation: {},
  isMobile: false,
  disableInventoryMLPhotoSort: false,
  selectedFacets: {},
  creativeId: '',
};

const stateToPropsConfig = {
  inventoryData: bindToPath(({ vin }) => `searchResultsVin["${vin}"]`, InventoryModel, null, false),
  visitorLocation: bindToPath('location', VisitorModel, null, false),
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  disableInventoryMLPhotoSort: !!state?.featureFlags?.['disable-inventory-ml-photo-sort'],
});

export const VinPreviewCard = flow(
  withInventoryListTracking,
  withListTrackingData,
  component => connectToModel(component, stateToPropsConfig),
  component =>
    connectToModel(component, {
      selectedFacets: bindToPath('searchResultsFilter', InventoryModel, null, false),
    }),
  connect(mapStateToProps)
)(VinPreviewCardUI);
